import React, { useReducer } from 'react';
import { Button, chakra, VStack } from '@chakra-ui/react';
import Select from 'react-select';

import { initStore, reducer, StoreData } from './store';
import { StemiInput } from '../../../../../components/Input';

interface SignUpFormProps {
  usernameError?: string;
  emailError?: string;
  onSubmit: (d: StoreData) => Promise<void>;
}

const emailRegex: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const countryOptions = [
  { value: 'hr', label: 'Croatia' },
  { value: 'other', label: 'Other' },
];
const roleOptions = [
  { value: 'student', label: 'Student' },
  { value: 'parent', label: 'Parent' },
  { value: 'teacher', label: 'Teacher' },
]

const _signUpForm: React.FC<SignUpFormProps> = (props) => {
  const { usernameError, emailError } = props;
  const [store, dispatch] = useReducer(reducer, initStore);

  const onChange =
    (key: keyof StoreData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        action: key,
        payload: e.target.value.trim(),
      });
    };

  const onCountryChange = (selectedOption: any) => {
    dispatch({
      action: 'country',
      payload: selectedOption?.value,
    });
  };

  const onRoleChange = (selectedOption: any) => {
    dispatch({
      action: 'role',
      payload: selectedOption?.value,
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('clicked on submit');

    if (store.username?.toLocaleLowerCase() !== store.username) {
      return dispatch({
        action: 'errors',
        payload: { username: 'Username should be lowercase' },
      });
    }

    if (store.username?.includes(' ')) {
      return dispatch({
        action: 'errors',
        payload: { username: 'Username should not include whitespaces' },
      });
    }

    if (!store.password) {
      return dispatch({
        action: 'errors',
        payload: { password: 'Password is required' },
      });
    }

    if (store.password && store.password.length < 6) {
      return dispatch({
        action: 'errors',
        payload: { password: 'Password is too short' },
      });
    }

    if (store.password !== store.confirmPassword) {
      return dispatch({
        action: 'errors',
        // prettier-ignore
        payload: { confirmPassword: 'Passwords don\'t match' },
      });
    }

    if (store.email && !emailRegex.test(store.email)) {
      return dispatch({
        action: 'errors',
        payload: { email: 'Email is not valid' },
      });
    }

    if (!store.country) {
      return dispatch({
        action: 'errors',
        payload: { country: 'Country is required' },
      });
    }

    if (store.country === 'other') {
      return dispatch({
        action: 'errors',
        payload: { country: 'Registration only available in Croatia at the moment' },
      });
    }

    dispatch({ action: 'errors', payload: {} });
    await props.onSubmit(store);
  };

  return (
    <chakra.form {...props} onSubmit={onSubmit}>
      <VStack spacing={0} gap={4}>
        <StemiInput
          noClass
          required
          label="Username"
          defaultValue={store.username}
          error={usernameError || store.errors['username']}
          onChange={onChange('username')}
        />
        <StemiInput
          noClass
          required
          label="Email"
          defaultValue={store.email}
          error={emailError || store.errors['email']}
          onChange={onChange('email')}
        />
        <StemiInput
          noClass
          showPasswordToggle
          type="password"
          label="Password"
          defaultValue={store.password}
          error={store.errors['password']}
          onChange={onChange('password')}
        />
        <StemiInput
          noClass
          showPasswordToggle
          type="password"
          label="Confirm password"
          defaultValue={store.confirmPassword}
          error={store.errors['confirmPassword']}
          onChange={onChange('confirmPassword')}
        />
        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            Country <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            styles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              }),
            }}
            placeholder="Select a country"
            options={countryOptions}
            value={countryOptions.find(option => option.value === store.country)}
            onChange={onCountryChange}
            isClearable
          />
          {store.errors['country'] && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
              {store.errors['country']}
            </div>
          )}
        </div>
        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
            Role <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            styles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              }),
            }}
            placeholder="Select a role"
            options={roleOptions}
            value={roleOptions.find(option => option.value === store.country)}
            onChange={onRoleChange}
            isClearable
          />
          {store.errors['role'] && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
              {store.errors['role']}
            </div>
          )}
        </div>
        <Button w="full" minW={0} colorScheme="blue" type="submit" size="lg">
          Sign up
        </Button>
      </VStack>
    </chakra.form>
  );
};

export const SignUpForm = chakra(_signUpForm);
